import React, { useState } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { isEmpty, isNil } from 'lodash';

import { ConfirmModal } from '~/components/ConfirmModal';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Tooltip from '~/components/core/Atomic/Tooltip';
import EmptyState from '~/components/core/EmptyState';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import OverflowArrayTextDisplayWithTooltip from '~/components/core/OverflowArrayTextWithTooltip/OverflowArrayTextDisplayWithTooltip';
import SortableTable from '~/components/core/Tables/SortableTable';
import Text from '~/components/core/TextComponents/Text';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import CheckIcon from '~/components/icons/CheckIcon';
import { AsyncSwitch } from '~/components/SystemConfiguration/Tabs/DeveloperTools/sections/VendorAPIProfiles/VendorAPIProfilesTable/AsyncSwitch';
import type { DocumentType } from '~/components/types/document-type';
import type { OrganizationModel } from '~/components/types/organization-types';
import type { SubOrganization } from '~/components/types/sub-organization-types';
import useDataFetcher from '~/components/useDataFetcher';
import type {
  AiConfigurationResponse,
  AiConfigurationResponseAiConfigurationResponseRecord,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';
import { getLobDescription } from '~/Utils/lobUtils';

import { useSysconfig } from '../SystemConfigurationScreen';

import { useSopConfiguration } from './SopConfiguration/hooks/useSopConfiguration';
import SopConfigurationDialog from './SopConfiguration/SopConfigurationDialog';
import SubOrgSopViewDialog from './SopConfiguration/SubOrgSopViewDialog';
import FallbackDocumentTypeDialog, { getFallbackDocumentTypeLabel } from './FallbackDocumentTypeDialog';

export type SwitchColumns = keyof Pick<
  AiConfigurationResponseAiConfigurationResponseRecord,
  | 'process_email_attachments'
  | 'claim_chat'
  | 'use_demo_fnol_policy_search'
  | 'use_demo_fnol_email_processing'
  | 'demo_acknowledgement_letter_action'
  | 'demo_adjuster_assignment_action'
  | 'send_ai_data'
  | 'process_api_documents'
  | 'create_document_with_defaults_on_failure'
  | 'skip_defaults_validation_on_classify_upload'
  | 'fnol_to_claim_stp'
  | 'clive_insights_claim_tab'
  | 'external_chat_api'
  | 'web_search_in_chat'
>;

export interface CliveConfigTableSwitchColumnConfig {
  id: SwitchColumns;
  label: string;
}

interface CliveConfigTableProps {
  config_switch_columns: Array<{
    id: SwitchColumns;
    label: string;
  }>;
}

// `has_sop_configuration` is a tri-state: the server answers `null` when it never established the
// status. Rendering that as an absent SOP would offer "Add SOP" over an existing one and skip the
// overwrite confirmation. Rows synthesised below for a sub-organization with no configuration record
// are given an explicit status from `is_sop_status_available` rather than left `undefined`, so an
// unknown here always means the AI service could not answer - never merely that nobody asked.
const isSopStatusUnknown = (row: Pick<AiConfigurationResponseAiConfigurationResponseRecord, 'has_sop_configuration'>) =>
  isNil(row.has_sop_configuration);

const SOP_STATUS_UNAVAILABLE_TOOLTIP = 'SOP status unavailable - the AI service could not be reached';

// A failed lookup is reported to the user directly, rather than left to be inferred from the table:
// the AI service being unreachable is a problem the person on the screen has to be told about, and
// nothing they do here can get an answer out of it.
const SOP_STATUS_UNAVAILABLE_TOAST =
  'SOP status could not be loaded - the AI service could not be reached. Rows with an unknown status show \u2014.';

const SOP_REPLACE_TEXT =
  'Replacing the existing SOP will permanently remove the current files. To keep any part of it, make sure to re-upload the relevant files in your new SOP.';
const SOP_STATUS_UNKNOWN_TEXT =
  'We could not check whether this sub-organization already has an SOP. If it does, continuing will permanently remove the current files.';
const ORG_LEVEL_SOP_REPLACED_TEXT =
  'There is also an existing SOP at the organization level, which creating a sub-organization SOP will replace.';

interface ConfigRow extends AiConfigurationResponseAiConfigurationResponseRecord {
  id: number;
  supported_lobs: SubOrganization['supported_lobs'] | OrganizationModel['supported_lobs'];
}

const CliveConfigTable: React.FC<CliveConfigTableProps> = ({ config_switch_columns }) => {
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  const { lobConfigurationsDict } = useLobConfiguration();
  const [isSubmitting, setIsSubmitting] = useState<number | null>(null);
  const [sopDialogSubOrg, setSopDialogSubOrg] = useState<ConfigRow | null>(null);
  const [viewDialogSubOrg, setViewDialogSubOrg] = useState<ConfigRow | null>(null);
  const [pendingSubOrgForConfirm, setPendingSubOrgForConfirm] = useState<ConfigRow | null>(null);
  const [isOrgLevelSopConfirmOpen, setIsOrgLevelSopConfirmOpen] = useState(false);
  const [isReplaceConfirmOpen, setIsReplaceConfirmOpen] = useState(false);
  const [fallbackDialogRow, setFallbackDialogRow] = useState<ConfigRow | null>(null);

  const baseConfigsRoute = `/sysconfig/api/v1/organizations/${organization?.id}/service_ai/configuration`;

  // Fetch org-level SOP configuration to check if it exists
  const { configuration: orgSopConfiguration } = useSopConfiguration();

  const {
    isLoading,
    isError,
    data: configResponse,
    reloadData: reloadSubOrganizationsConfig,
  } = useDataFetcher<AiConfigurationResponse>(baseConfigsRoute);

  const isSopStatusAvailable = configResponse?.is_sop_status_available;
  React.useEffect(() => {
    if (isSopStatusAvailable === false) {
      // Keyed, so a re-render or a reload that fails the same way does not stack a second copy.
      toast.error(SOP_STATUS_UNAVAILABLE_TOAST, { toastId: 'clive-sop-status-unavailable' });
    }
  }, [isSopStatusAvailable]);

  // The same enabled-only set the sysconfig route validates a selection against, so the picker
  // cannot offer a type the PUT would reject.
  const { data: enabledDocumentTypesDict } = useDataFetcher<Record<string, DocumentType>>(
    `/api/v1/document_types/organizations/${organization?.id}`,
    { params: { is_enabled: true } }
  );

  const configRowsWithMissingRows = React.useMemo<ConfigRow[] | null>(() => {
    if (!configResponse) {
      return null;
    }

    const configs: ConfigRow[] = [];

    const defaultValues = {
      organization_external_id: organization.external_id,
      organization_id: organization.id,
      process_email_attachments: false,
      claim_chat: false,
      use_demo_fnol_policy_search: false,
      use_demo_fnol_email_processing: false,
      demo_acknowledgement_letter_action: false,
      demo_adjuster_assignment_action: false,
      send_ai_data: false,
      process_api_documents: false,
      create_document_with_defaults_on_failure: false,
      skip_defaults_validation_on_classify_upload: false,
      fnol_to_claim_stp: false,
      clive_insights_claim_tab: false,
      external_chat_api: false,
      web_search_in_chat: false,
      fallback_document_type_key: null,
      // A sub-organization with no configuration record still has a knowable SOP status: the AI
      // service answers with every sub-account that has one, so an id absent from that answer has
      // none. Only an answer that never arrived leaves the status genuinely unknown.
      has_sop_configuration: configResponse.is_sop_status_available ? false : null,
    };
    if (organization.sub_organizations?.length) {
      const existingSubOrgConfigIds = configResponse.configurations.map((c) => c.sub_organization_id);
      organization.sub_organizations.forEach((subOrg) => {
        if (!existingSubOrgConfigIds.includes(subOrg.id)) {
          configs.push({
            id: subOrg.id,
            sub_organization_id: subOrg.id,
            description: subOrg.name || '',
            sub_organization_external_id: subOrg.external_id,
            supported_lobs: subOrg.supported_lobs || [],
            ...defaultValues,
          });
        } else {
          const existingConfig = configResponse.configurations.find((c) => c.sub_organization_id === subOrg.id);
          if (existingConfig) {
            configs.push({
              ...existingConfig,
              id: subOrg.id,
              supported_lobs: subOrg.supported_lobs || [],
            });
          }
        }
      });
    } else if (!organization.sub_organizations_enabled) {
      configs.push({
        id: organization.id,
        description: organization.name || '',
        supported_lobs: organization.supported_lobs || [],
        ...(isEmpty(configResponse.configurations) ? defaultValues : configResponse.configurations[0]),
      });
    }

    return configs.sort((a, b) => a.id - b.id);
  }, [configResponse, organization]);

  const hasOrgLevelSop = !!orgSopConfiguration;

  const handleSopAction = (row: ConfigRow) => {
    const hasExistingSubOrgSop = row.has_sop_configuration;

    if (hasExistingSubOrgSop || isSopStatusUnknown(row)) {
      // Sub-org already has SOP, or we could not find out - either way, confirm before overwriting.
      // When the status is unknown the confirmation also carries the org-level warning below, which
      // this branch would otherwise skip.
      setPendingSubOrgForConfirm(row);
      setIsReplaceConfirmOpen(true);
    } else if (hasOrgLevelSop) {
      // No sub-org SOP but org has SOP - show org-level warning
      setPendingSubOrgForConfirm(row);
      setIsOrgLevelSopConfirmOpen(true);
    } else {
      // No existing SOP anywhere - open dialog directly
      setSopDialogSubOrg(row);
    }
  };

  // The pending row is left in place once a confirmation closes rather than cleared: MUI's Dialog
  // stays mounted through its exit transition, so clearing it here would flip the title and body back
  // to their default wording while the modal is still on screen. handleSopAction sets it afresh on
  // every open, so nothing reads a stale row.
  const proceedToSopDialog = () => {
    if (pendingSubOrgForConfirm) {
      setSopDialogSubOrg(pendingSubOrgForConfirm);
    }
  };

  const handleOrgLevelSopConfirm = () => {
    setIsOrgLevelSopConfirmOpen(false);
    proceedToSopDialog();
  };

  const isPendingSopStatusUnknown = !!pendingSubOrgForConfirm && isSopStatusUnknown(pendingSubOrgForConfirm);

  const handleReplaceConfirm = () => {
    setIsReplaceConfirmOpen(false);
    proceedToSopDialog();
  };

  const handleSwitchChange = async (row: ConfigRow, field: SwitchColumns, value: boolean): Promise<void> => {
    try {
      setIsSubmitting(row.id);
      row[field] = value;

      const url = [baseConfigsRoute, row.sub_organization_id].filter(Boolean).join('/');

      await axios.put(url, row);
      await reloadSubOrganizationsConfig();
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      await reloadSubOrganizationsConfig();
      setIsSubmitting(null);
    }
  };

  const handleFallbackDocumentTypeChange = async (
    row: ConfigRow,
    fallbackDocumentTypeKey: string | null
  ): Promise<void> => {
    try {
      setIsSubmitting(row.id);
      const url = [baseConfigsRoute, row.sub_organization_id].filter(Boolean).join('/');

      // A copy rather than a mutation of the memoized row: the picker's value comes from the
      // reloaded response, so there is nothing to keep in sync locally.
      await axios.put(url, { ...row, fallback_document_type_key: fallbackDocumentTypeKey });
      setFallbackDialogRow(null);
    } catch (error) {
      await reportAxiosError(error);
    } finally {
      await reloadSubOrganizationsConfig();
      setIsSubmitting(null);
    }
  };

  const defaultColumns = [
    {
      id: 'id',
      label: 'Id',
    },
    {
      id: 'description',
      label: 'Name',
    },
    {
      id: organization.sub_organizations_enabled ? 'sub_organization_external_id' : 'organization_external_id',
      label: 'External Id',
    },
    {
      id: 'lobs',
      label: 'Lines of Business',
      specialCell: (row: SubOrganization) => (
        <OverflowArrayTextDisplayWithTooltip
          value={(row.supported_lobs || []).map((supported_lob) =>
            getLobDescription(supported_lob.lob || '', lobConfigurationsDict)
          )}
          renderItem={(label) => (
            <span key={label}>
              <Chip label={label} size="small" className="mr-12" />
            </span>
          )}
        />
      ),
      disableSort: true,
    },
    {
      id: 'sop_status',
      label: 'SOP',
      width: '80px',
      specialCell: (row: ConfigRow) =>
        isSopStatusUnknown(row) ? (
          <Tooltip title={SOP_STATUS_UNAVAILABLE_TOOLTIP}>
            <span>&mdash;</span>
          </Tooltip>
        ) : row.has_sop_configuration ? (
          <CheckIcon color="success" />
        ) : null,
      disableSort: true,
    },
  ];

  const actionsColumn = {
    id: 'actions',
    label: 'Actions',
    specialCell: (row: ConfigRow) => {
      const menuOptions = [];

      if (row.has_sop_configuration || isSopStatusUnknown(row)) {
        menuOptions.push({
          key: 'view_sop',
          node: 'View SOP',
          onClick: () => setViewDialogSubOrg(row),
        });
      }

      menuOptions.push({
        key: 'sop_action',
        node: isSopStatusUnknown(row) ? 'Add / Replace SOP' : row.has_sop_configuration ? 'Replace SOP' : 'Add SOP',
        onClick: () => handleSopAction(row),
      });

      menuOptions.push({
        key: 'fallback_document_type',
        node: 'Set Classification Fallback',
        onClick: () => setFallbackDialogRow(row),
      });

      return <ThreeDotsMenu options={menuOptions} />;
    },
    disableSort: true,
  };

  const switchColumns = config_switch_columns.map((column) => ({
    id: column.id,
    label: column.label,
    specialCell: (row: ConfigRow) => {
      return (
        <Tooltip title={column.label}>
          <span>
            <AsyncSwitch
              disabled={isSubmitting === row.id}
              checked={Boolean(row[column.id])}
              onChange={(e) => handleSwitchChange(row, column.id, e.target.checked)}
            />
          </span>
        </Tooltip>
      );
    },
  }));

  const fallbackDocumentTypeColumn = {
    id: 'fallback_document_type_key',
    label: 'Classification Fallback Document Type',
    specialCell: (row: ConfigRow) => (
      <Text variant="small" weight="regular">
        {getFallbackDocumentTypeLabel(row.fallback_document_type_key, enabledDocumentTypesDict || {})}
      </Text>
    ),
    disableSort: true,
  };

  const processedColumns = [...defaultColumns, ...switchColumns, fallbackDocumentTypeColumn, actionsColumn];

  return (
    <LoadingSwitch isLoading={isLoading} isError={isError}>
      <div className="bg-white p-20">
        <SortableTable
          columns={processedColumns}
          rows={configRowsWithMissingRows || []}
          stickyHeader
          emptyStateComponent={<EmptyState title="No Sub-Organizations" />}
        />
      </div>
      <SopConfigurationDialog
        open={!!sopDialogSubOrg}
        onClose={() => setSopDialogSubOrg(null)}
        subOrganizationId={sopDialogSubOrg?.sub_organization_id ?? undefined}
        subOrganizationName={sopDialogSubOrg?.description}
        onSaveSuccess={reloadSubOrganizationsConfig}
        // Anything but a definite "no SOP" is an overwrite: an unknown status is exactly the case
        // where an existing SOP may be replaced, so it must not be tracked as a fresh save.
        isReplacing={!!sopDialogSubOrg && sopDialogSubOrg.has_sop_configuration !== false}
      />
      {fallbackDialogRow && (
        <FallbackDocumentTypeDialog
          configurationName={fallbackDialogRow.description || ''}
          fallbackDocumentTypeKey={fallbackDialogRow.fallback_document_type_key}
          documentTypesDict={enabledDocumentTypesDict || {}}
          isSubmitting={isSubmitting === fallbackDialogRow.id}
          onSave={(fallbackDocumentTypeKey) =>
            handleFallbackDocumentTypeChange(fallbackDialogRow, fallbackDocumentTypeKey)
          }
          onClose={() => setFallbackDialogRow(null)}
        />
      )}
      {viewDialogSubOrg && (
        <SubOrgSopViewDialog
          open={true}
          onClose={() => setViewDialogSubOrg(null)}
          subOrganizationId={viewDialogSubOrg.sub_organization_id!}
          subOrganizationName={viewDialogSubOrg.description!}
        />
      )}
      {/* Confirmation dialog when org has existing SOP and user clicks Add at sub-org level */}
      <ConfirmModal
        isOpen={isOrgLevelSopConfirmOpen}
        onClose={() => setIsOrgLevelSopConfirmOpen(false)}
        title="Create SOP at Sub-Organization level?"
        contentText="There is an existing SOP at the organization level. Creating a new SOP for this sub-organization will replace the organization-level SOP. If you want to keep anything from the current organization-level SOP, please re-upload the relevant files when creating the new one."
        primaryButtonName="Continue"
        secondaryButtonName="Cancel"
        onPrimaryBtnClick={handleOrgLevelSopConfirm}
      />

      {/* Confirmation dialog when replacing existing sub-org SOP */}
      <ConfirmModal
        isOpen={isReplaceConfirmOpen}
        onClose={() => setIsReplaceConfirmOpen(false)}
        title={isPendingSopStatusUnknown ? 'SOP status unavailable' : 'Replace SOP'}
        contentText={
          isPendingSopStatusUnknown
            ? `${SOP_STATUS_UNKNOWN_TEXT}${hasOrgLevelSop ? ` ${ORG_LEVEL_SOP_REPLACED_TEXT}` : ''}`
            : SOP_REPLACE_TEXT
        }
        primaryButtonName="Continue"
        secondaryButtonName="Cancel"
        onPrimaryBtnClick={handleReplaceConfirm}
      />
    </LoadingSwitch>
  );
};

export default CliveConfigTable;
